import { Injectable } from '@angular/core';
import { HttpClient,HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthenticationService ,AppConfigService} from '@alfresco/adf-core';


export interface ParticipantDto {
  id: string;
  displayName: string;
  email?: string;
  order: number;
  status: string; // pending | in_review | completed | rejected
}

export interface UserDto {
  id: string;
  displayName: string;
  email?: string;
}

export interface ForwardTaskRequest {
  additionalReviewers: ParticipantDto[];
  comments?: string;
}

export interface WorkflowStatusResponse {
  workflowStarted: boolean;
  processInstanceId?: string;
  currentStage?: string;
  currentAssignee?: string;
  currentAssigneeName?: string;
  status?: string;
  activeTaskId?: string;
  activeTaskType?: string;
  myTask: boolean;
  startedAt?: string;
  reviewersTotal: number;
  reviewersCompleted: number;
  approversTotal: number;
  approversCompleted: number;
  reviewers: ParticipantDto[];
  approvers: ParticipantDto[];
  pdfNodeId?: string;
  contractNodeId?: string;
  contractNumber?: string;
  contractTitle?: string;
  supplierName?: string;
}

export interface WorkflowTaskDto {
  taskId: string;
  taskType: string;
  contractNodeId: string;
  contractNumber: string;
  contractTitle: string;
  supplierName: string;
  contractStatus: string;
  assignee: string;
  assigneeName: string;
  createdAt: string;
  dueDate?: string;
  overdue: boolean;
  currentStage: string;
  reviewStep: number;
  reviewTotal: number;
}

export interface TaskDetailResponse {
  taskId: string;
  taskType: string;
  taskName: string;
  assignee: string;
  assigneeName: string;
  createdAt: string;
  dueDate?: string;
  contractNodeId: string;
  pdfNodeId?: string;
  contractNumber: string;
  contractTitle: string;
  supplierName: string;
  contractStatus: string;
  contractAmount?: number;
  currency?: string;
  effectiveDate?: string;
  expiryDate?: string;
  departmentName?: string;
  contractType?: string;
  reviewers: ParticipantDto[];
  approvers: ParticipantDto[];
  reviewStep: number;
  reviewTotal: number;
  approvalStep: number;
  approvalTotal: number;
  comments: CommentDto[];
  signatures: SignatureRecord[];
}

export interface TaskActionRequest {
  taskId?: string;
  decision: string;
  comments?: string;
  signatureRef?: string;
  reviewers?: ParticipantDto[];
  approvers?: ParticipantDto[];
}

export interface CommentDto {
  id: number;
  authorUsername: string;
  authorName: string;
  content: string;
  createdAt: string;
  parentId?: number;
  replies: CommentDto[];
}

export interface SignatureRecord {
  signerUsername: string;
  signerName: string;
  signOrder: number;
  signedAt: string;
  signatureRef?: string;
}

export interface TaskCountsResponse {
  REVIEW: number;
  APPROVAL: number;
  ARCHIVE: number;
  CONFIGURE: number;
  AMEND: number;
}

@Injectable({ providedIn: 'root' })
export class ContractWorkflowService {
   private readonly base: string;

  // Annotator viewer URL - populated from app.config.json annotatorUrl
  // (full viewer URL, e.g. "http://localhost:8092/annotator/viewer")
  private readonly annotatorUrl: string;

  readonly taskCounts$ = new BehaviorSubject<TaskCountsResponse>({
    REVIEW: 0, APPROVAL: 0, ARCHIVE: 0, CONFIGURE: 0, AMEND: 0
  });

   constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${apiBase}/contracts/workflow`;

    // Annotator URL — falls back to the dev default if the key is missing.
    let annotatorUrl = this.appConfig.get<string>('annotatorUrl', '')
      || 'http://localhost:8092/annotator/viewer';
    if (annotatorUrl.endsWith('/')) {
      annotatorUrl = annotatorUrl.slice(0, -1);
    }
    this.annotatorUrl = annotatorUrl;
  }

  private getHeaders() {
    const token = this.authService.getToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });
  }

  startWorkflow(request: any): Observable<WorkflowStatusResponse> {
    return this.http.post<any>(`${this.base}/start`, request, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow));
  }

  getWorkflowStatus(contractNodeId: string): Observable<WorkflowStatusResponse> {
    return this.http.get<any>(`${this.base}/${contractNodeId}/status`, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow));
  }

  /** Tasks assigned to ME in Flowable — populates REVIEW / APPROVAL / ARCHIVE columns */
  getMyTasks(type?: string): Observable<WorkflowTaskDto[]> {
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get<any>(`${this.base}/my-tasks`, { headers: this.getHeaders(), params })
      .pipe(map(r => r.tasks));
  }

  /**
   * Workflows I STARTED — built from ContractWorkflowState, not Flowable task queue.
   * Visible to the initiator even when another user (reviewer/approver) has the active task.
   */
  getInitiatedWorkflows(): Observable<WorkflowTaskDto[]> {
    return this.http.get<any>(`${this.base}/my-initiated`, { headers: this.getHeaders() })
      .pipe(map(r => r.tasks));
  }

  refreshTaskCounts(): void {
    this.http.get<any>(`${this.base}/my-tasks/counts`, { headers: this.getHeaders() })
      .pipe(map(r => r.counts))
      .subscribe({ next: c => this.taskCounts$.next(c), error: e => console.error(e) });
  }

  getTaskDetail(taskId: string): Observable<TaskDetailResponse> {
    return this.http.get<any>(`${this.base}/task/${taskId}`, { headers: this.getHeaders() })
      .pipe(map(r => r.task));
  }

  completeTask(taskId: string, request: TaskActionRequest): Observable<WorkflowStatusResponse> {
    return this.http.post<any>(`${this.base}/task/${taskId}/complete`, request, { headers: this.getHeaders() })
      .pipe(map(r => r.workflow), tap(() => this.refreshTaskCounts()));
  }

  getComments(contractNodeId: string): Observable<CommentDto[]> {
    return this.http.get<any>(`${this.base}/${contractNodeId}/comments`, { headers: this.getHeaders() })
      .pipe(map(r => r.comments));
  }

  addComment(taskId: string, contractNodeId: string, content: string, parentId?: number): Observable<CommentDto> {
    return this.http.post<any>(`${this.base}/task/${taskId}/comment`,
      { contractNodeId, content, parentId }, { headers: this.getHeaders() })
      .pipe(map(r => r.comment));
  }

  /**
   * Opens the SkyPlus Annotator in a popup window to sign/annotate a document.
   * Mirrors SkyPlusDocumentLibraryComponent.signDocument() — uses the
   * authenticated ECM username and the Alfresco nodeId to build the URL.
   *
   * Optionally polls popup.closed every 1 s and fires onClose() when
   * the popup is dismissed, so callers can refresh their state
   * (e.g. reload the PDF panel with the signed copy).
   *
   * @param documentNodeId Alfresco nodeId of the document to sign
   *                       (typically TaskDetailResponse.pdfNodeId)
   * @param onClose        Optional callback fired when the popup closes
   *                       (signed or cancelled). Omit for fire-and-forget.
   */
  signDocument(documentNodeId: string, onClose?: () => void): void {
    if (!documentNodeId) return;

    const win = this.openAnnotatorWindow(documentNodeId);

    // Fire-and-forget mode (matches doc-library exactly when no callback supplied)
    if (!onClose) return;

    // Callback mode — preserve contract-detail's reload-after-signing behaviour
    if (!win) {
      alert('Please allow popups for this site to use the signing feature.');
      onClose();
      return;
    }
    const timer = setInterval(() => {
      if (win.closed) { clearInterval(timer); onClose(); }
    }, 1000);
  }

  /**
   * Opens the annotator popup. Opens synchronously (so the click gesture is
   * preserved and it isn't popup-blocked) to a loading page, then navigates to a
   * signed viewer URL when the backend has signed-access enabled, otherwise to the
   * legacy ?userId= URL (today's behaviour). Non-breaking: disabled / 404 / any
   * error all fall back to the legacy URL.
   */
  private openAnnotatorWindow(documentNodeId: string): Window | null {
    const userId = this.authService.getEcmUsername() || 'unknown';
    const legacyUrl = `${this.annotatorUrl}?docId=${documentNodeId}&userId=${userId}`;
    const features = 'width=1300,height=900,scrollbars=yes,resizable=yes';
    const popup = window.open('', 'SignDocument', features);
    if (popup) {
      try { popup.document.write('<!doctype html><title>Loading…</title><body style="font:14px sans-serif;padding:24px">Loading document…</body>'); } catch { /* ignore */ }
    }
    const go = (url: string) => { if (popup) { popup.location.href = url; } };
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.http.post<any>(`${apiBase}/signing/viewer-url`, { docId: documentNodeId, scope: 'annotate' }, { headers: this.getHeaders() })
      .subscribe({
        next: (resp) => go(resp && resp.enabled && resp.token
          ? `${this.annotatorUrl}?docId=${encodeURIComponent(documentNodeId)}&token=${encodeURIComponent(resp.token)}`
          : legacyUrl),
        error: () => go(legacyUrl)
      });
    return popup;
  }

  getOnlyOfficeConfig(taskId: string): Observable<any> {
    return this.http.get<any>(`${this.base}/onlyoffice/config/${taskId}`, { headers: this.getHeaders() }).pipe(
      map((config: any) => ({
        ...config,
        // _documentServerUrl is a frontend-only helper — tells the component where
        // to load api.js from. It is stripped before passing config to DocsAPI.
        _documentServerUrl: environment.onlyOfficeUrl
      }))
    );
  }

  /**
   * Forward the current REVIEW task to additional reviewers.
   * Appends them to the reviewerList process variable and completes the current task.
   */
  forwardTask(taskId: string, request: ForwardTaskRequest): Observable<any> {
    return this.http.post<any>(`${this.base}/tasks/${taskId}/forward`, request, { headers: this.getHeaders() });
  }

}